import type {
  Question,
  QuestionTreeResponse,
  QuestionType,
  QuestionVisibility,
} from "../types/questionTree.types";

/**
 * Adapter del árbol médico NATIVE (ms-agl) → `QuestionTreeResponse` que consume
 * `useQuestionTree` (F5b). Vive en el FRONT porque la resolución de textos
 * (`content`, labels de opciones) necesita i18n, que es front-side — el front
 * pinta `question.content` CRUDO, y ms-agl solo entrega `contentKey`.
 *
 * ms-agl entrega por pregunta: `{field, type, component, contentKey,
 * prefilledCategory, options:[{key,value}], visibility}`. La `visibility` es
 * "idéntica al contrato del Question BC" del monolito, así que se pasa tal cual
 * (gating `Lead::gender`). `type` de ms-agl (`number`, `checkbox_optional`) ya
 * coincide con `QuestionType`, sin tabla de mapeo.
 *
 * DOS PUNTOS A VERIFICAR EN INTEGRACIÓN (no verificables aquí: las traducciones
 * viven en el repo externo getlife/translations, dominio `questions_stack_AGL_IT`):
 *   1. Convención de clave i18n: se asume `<contentKey>.title/.subtitle/.tooltip`
 *      (documentada en el árbol F1). Fallback al propio `contentKey` si falta.
 *   2. Si el `value` de una opción es texto ya localizado o una clave i18n:
 *      `translate(value) || value` cubre ambos.
 */

interface NativeOption {
  readonly key: string;
  readonly value: string;
}

export interface NativeMedicalQuestion {
  readonly field: string;
  readonly type: string;
  readonly component?: string | null;
  readonly contentKey: string;
  readonly prefilledCategory?: string | null;
  readonly options?: NativeOption[];
  readonly visibility?: QuestionVisibility | null;
}

export interface NativeMedicalTree {
  readonly carrier: string;
  readonly market: string;
  readonly version: number;
  readonly gender?: string | null;
  readonly questions: NativeMedicalQuestion[];
}

type Translate = (key: string) => string;

/**
 * Preguntas de COMPLIANCE sintéticas (F5b enforce): residenza en Italia (Q3) y
 * PEP (Q4). ms-agl NO tiene árbol de product-questions, así que se añaden en el
 * front al final del cuestionario para que fluyan por el mismo hook → confirm →
 * y la UW native las evalúe (toInput lee estos fields: 'no' en residencia o
 * 'yes' en PEP → rechaza). Valores sì/no calcados del monolito. Aparecen en el
 * paso de preguntas (no en un paso de producto aparte) — placement a revisar.
 */
/**
 * Preguntas de PRODUCTO plegadas en el paso de preguntas (decisión Jose): la
 * intención (display/tracking, se pinta en la confirmación) y la fecha de efecto
 * (fija la decorrenza de la póliza; la respuesta la usa el select del upsell para
 * materializar el fee_schedule con esa fecha — y se puede cambiar en el upsell).
 * Van al PRINCIPIO, como en el funnel del monolito (producto antes del médico).
 */
function productQuestions(translate: Translate): Question[] {
  return [
    {
      id: 0,
      name: "Lead::intention",
      type: "radio",
      content:
        translate("productQuestions.intention.title") ||
        "Che cosa vorresti proteggere?",
      field: "Lead::intention",
      required: true,
      product_type: "product",
      options: [
        { value: "family", label: translate("confirmationPage.intention.family") || "La mia famiglia" },
        { value: "loan", label: translate("confirmationPage.intention.loan") || "Il mio mutuo" },
        { value: "other", label: translate("productQuestions.intention.other") || "Altro" },
      ],
    },
    {
      id: 0,
      name: "Lead::effectDate",
      type: "calendar_extended",
      content:
        translate("productQuestions.effectDate.title") ||
        "Da quando vorresti iniziare ad essere assicurato con noi?",
      field: "Lead::effectDate",
      required: true,
      product_type: "product",
    },
  ];
}

function complianceQuestions(startId: number, translate: Translate): Question[] {
  const yesNo = [
    { value: "yes", label: translate("yes") || "Sì" },
    { value: "no", label: translate("no") || "No" },
  ];
  return [
    {
      id: startId,
      name: "LeadCustom::it.life5.residence-in-italy",
      type: "radio",
      content:
        translate("organic.questions.residenceInItaly") ||
        "Risiedi attualmente in Italia?",
      field: "LeadCustom::it.life5.residence-in-italy",
      required: true,
      product_type: "compliance",
      options: yesNo,
    },
    {
      id: startId + 1,
      name: "LeadCustom::it.agl.prp",
      type: "radio",
      content:
        translate("organic.questions.prp") ||
        "Sei una Persona Politicamente Esposta (PEP)?",
      field: "LeadCustom::it.agl.prp",
      required: true,
      product_type: "compliance",
      options: yesNo,
    },
  ];
}

/**
 * Fallback IT por CAMPO para los títulos de las preguntas médicas cuando la
 * traducción (`<contentKey>.title`) aún no está en getlife/translations. Copy
 * provisional (revisar con producto); evita pintar la key cruda al cliente.
 */
const FIELD_TITLE_FALLBACK: Record<string, string> = {
  "LeadHealth::height": "Quanto sei alto/a? (in cm)",
  "LeadHealth::weight": "Quanto pesi? (in kg)",
  "LeadCustom::it.agl.illness": "Hai mai sofferto o soffri di una di queste patologie?",
  "LeadCustom::it.agl.illness_male": "Hai mai sofferto o soffri di una di queste patologie?",
  "LeadCustom::it.agl.illness_female": "Hai mai sofferto o soffri di una di queste patologie?",
};

export function adaptNativeMedicalTree(
  tree: NativeMedicalTree,
  translate: Translate
): QuestionTreeResponse {
  // `getTranslate` devuelve la PROPIA key cuando no hay traducción (`|| key`), así
  // que `translate(k) || fallback` nunca cae al fallback. `t()` trata la key-echo
  // como "sin traducción" (cadena vacía) para que los fallbacks y la omisión de
  // subtítulos/tooltips funcionen.
  const t = (key: string): string => {
    const value = translate(key);
    return value === key ? "" : value;
  };
  const questions: Question[] = tree.questions.map((q) => {
    const subtitle = t(`${q.contentKey}.subtitle`);
    const tooltip = t(`${q.contentKey}.tooltip`);
    return {
      id: 0,
      name: q.field,
      type: q.type as QuestionType,
      content:
        t(`${q.contentKey}.title`) || FIELD_TITLE_FALLBACK[q.field] || q.contentKey,
      field: q.field,
      // height/weight (number) son obligatorias (IMC); las illness
      // (checkbox_optional) no: no marcar = 'none' = acepta.
      required: "number" === q.type,
      product_type: "health",
      ...("" === subtitle ? {} : { subtitle }),
      ...("" === tooltip ? {} : { tooltip }),
      ...(null === q.component || undefined === q.component
        ? {}
        : { component_name: q.component }),
      options: (q.options ?? []).map((o) => ({
        value: o.key,
        label: t(o.value) || o.value,
      })),
      ...(null === q.visibility || undefined === q.visibility
        ? {}
        : { visibility: q.visibility }),
    };
  });

  // Orden: producto (intención + fecha) → médicas → compliance. Los ids se
  // reasignan secuencialmente sobre el conjunto (la visibilidad gira por field,
  // no por id, así que renumerar es seguro).
  const ordered = [
    ...productQuestions(t),
    ...questions,
    ...complianceQuestions(0, t),
  ].map((q, index) => ({ ...q, id: index + 1 }));

  return {
    metadata: {
      carrier: tree.carrier,
      market: tree.market,
      version: tree.version,
      locale: "it",
    },
    questions: ordered,
    // Lead organic recién nacido: sin respuestas previas que prefillar.
    answers: [],
    // El engine de visibilidad resuelve la regla `Lead::gender` (source:lead) por
    // el NOMBRE CORTO del campo (`field.split('::').pop()` → `gender`), no por el
    // field completo. Se publican ambas claves para que el gate de illness_male
    // (gender=Male) funcione.
    lead:
      null === tree.gender || undefined === tree.gender || "" === tree.gender
        ? {}
        : { "Lead::gender": tree.gender, gender: tree.gender },
  };
}
